import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  IconButton,
  TextField,
  Button,
  Collapse,
  Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Link from "@mui/material/Link";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Layout from "../../layout/Layout";
import { config, employeeAttandenceAPI, userData } from "../../config";

// Super-admin (role 34) report: every active employee's attendance for the
// selected 26->25 salary cycle, with the five counts + an expandable per-day
// strip. Data: POST /attandence-logout intType 20 (all-employees + single).
const DAY_COLORS = {
  worked: { bg: "#daf1e2", fg: "#15a34a" },
  half: { bg: "#e6f3ec", fg: "#15a34a" },
  leave: { bg: "#eae7fa", fg: "#6d5fd6" },
  comp: { bg: "#fbead0", fg: "#d9820a" },
  lop: { bg: "#fbdfe5", fg: "#dc4b66" },
  absent: { bg: "#f6e4e8", fg: "#b0455c" },
  holiday: { bg: "#eef1f6", fg: "#8a97ad" },
  weekoff: { bg: "#eef1f6", fg: "#8a97ad" },
  future: { bg: "#f6f8fa", fg: "#c4d0cf" },
};
const PILL = {
  worked: { bg: "#daf1e2", fg: "#15a34a" },
  paidLeave: { bg: "#eae7fa", fg: "#6d5fd6" },
  lop: { bg: "#fbdfe5", fg: "#dc4b66" },
  permission: { bg: "#d6f2ee", fg: "#0d9488" },
  compOff: { bg: "#fbead0", fg: "#d9820a" },
};
const num = (n) => (Number.isInteger(Number(n)) ? String(n) : Number(n).toFixed(1));

function Pill({ v, kind }) {
  const c = PILL[kind];
  if (!v) return <span style={{ color: "#aab" }}>—</span>;
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 26,
        padding: "3px 8px",
        borderRadius: 7,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        background: c.bg,
        color: c.fg,
      }}
    >
      {num(v)}
    </span>
  );
}

function DayStrip({ empId, cycleDate }) {
  const [days, setDays] = useState(null);
  useEffect(() => {
    config.method = "POST";
    config.url = employeeAttandenceAPI;
    config.data = JSON.stringify({
      intType: 20,
      intEmployeeID: empId,
      txtCycleDate: cycleDate,
    });
    axios(config)
      .then((r) => {
        const payload = r.data.result && r.data.result[0];
        setDays((payload && payload.days) || []);
      })
      .catch((e) => {
        console.log(e);
        setDays([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId, cycleDate]);

  if (days === null)
    return (
      <Typography sx={{ fontSize: 12, color: "text.secondary", py: 1 }}>
        Loading…
      </Typography>
    );
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "3px", py: 1 }}>
      {days.map((d) => {
        const c = DAY_COLORS[d.status] || DAY_COLORS.future;
        return (
          <Tooltip key={d.date} title={`${moment(d.date).format("DD MMM")} · ${d.status}${d.hasPermission ? " · permission" : ""}`}>
            <Box
              sx={{
                width: 26,
                height: 30,
                borderRadius: "6px",
                background: c.bg,
                color: c.fg,
                fontSize: 10,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {d.day}
              {d.hasPermission && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 3,
                    right: 3,
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#0d9488",
                  }}
                />
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function AllEmployeesAttendance() {
  const [anchor, setAnchor] = useState(moment());
  const [rows, setRows] = useState([]);
  const [cycle, setCycle] = useState({ start: null, end: null, workingDays: 0 });
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAllowed = Number(userData?.role_id) === 34;

  const load = (anchorMoment) => {
    setLoading(true);
    setExpanded(null);
    config.method = "POST";
    config.url = employeeAttandenceAPI;
    config.data = JSON.stringify({
      intType: 20,
      intEmployeeID: 0,
      txtCycleDate: anchorMoment.format("YYYY-MM-DD"),
    });
    axios(config)
      .then((r) => {
        setRows(r.data.result || []);
        setCycle({
          start: r.data.cycleStart || null,
          end: r.data.cycleEnd || null,
          workingDays: r.data.workingDays || 0,
        });
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  };

  const tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false && isAllowed) {
      load(anchor);
      tempRefs.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = (dir) => {
    const next = anchor.clone().add(dir, "month");
    setAnchor(next);
    load(next);
  };

  const cycleLabel =
    cycle.start && cycle.end
      ? `${moment(cycle.start).format("DD MMM")} – ${moment(cycle.end).format("DD MMM YYYY")}`
      : "—";

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.employee_name || "").toLowerCase().includes(q) ||
      (r.employee_no || "").toLowerCase().includes(q)
    );
  });

  // Summary tiles.
  const totalEmp = filtered.length;
  const avgWorked =
    totalEmp > 0
      ? (filtered.reduce((s, r) => s + (Number(r.worked) || 0), 0) / totalEmp).toFixed(1)
      : "0";
  const totalLeave = filtered.reduce(
    (s, r) => s + (Number(r.paidLeave) || 0) + (Number(r.lop) || 0),
    0
  );
  const totalComp = filtered.reduce((s, r) => s + (Number(r.compOff) || 0), 0);

  const exportExcel = () => {
    const data = filtered.map((r) => ({
      "Employee No": r.employee_no,
      Employee: r.employee_name,
      Company: r.company_name,
      Worked: r.worked,
      "Paid Leave": r.paidLeave,
      LOP: r.lop,
      Permission: r.permission,
      "Comp-off": r.compOff,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(
      wb,
      `attendance_${cycle.start || "cycle"}_${cycle.end || ""}.xlsx`
    );
  };

  const tile = (n, t, d, color) => (
    <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, minWidth: 150 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color }}>{n}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334" }}>{t}</Typography>
        <Typography sx={{ fontSize: 11, color: "#7d8f8e" }}>{d}</Typography>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={7} md={7}>
            <Typography className="page_title" textAlign="left">
              All Employees — Monthly Attendance
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Attendance</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-40"></div>

        {!isAllowed ? (
          <Typography color="error" sx={{ mt: 2 }}>
            You are not authorized to view this page.
          </Typography>
        ) : (
          <>
            {/* Toolbar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton size="small" onClick={() => move(-1)}>
                  <ChevronLeftIcon />
                </IconButton>
                <Box sx={{ textAlign: "center", minWidth: 190 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    {cycleLabel}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#7d8f8e" }}>
                    26→25 cycle · {cycle.workingDays || "—"} working days
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => move(1)}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <TextField
                size="small"
                placeholder="Search employee…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={exportExcel}
                disabled={filtered.length === 0}
              >
                Export
              </Button>
            </Box>

            {/* Summary tiles */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              {tile(totalEmp, "Employees", "in this cycle", "#2b57d9")}
              {tile(avgWorked, "Avg days worked", `of ${cycle.workingDays || "—"} working days`, "#15a34a")}
              {tile(totalLeave, "Leave days", "paid + LOP, all staff", "#6d5fd6")}
              {tile(totalComp, "Comp-off earned", "worked on offs", "#d9820a")}
            </Box>

            {/* Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f4f6fb" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Worked</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Paid leave</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">LOP</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Permission</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Comp-off</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        No employees found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((r) => (
                      <React.Fragment key={r.employee_id}>
                        <TableRow hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                              {r.employee_name || `#${r.employee_id}`}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#8a97ad" }}>
                              {r.employee_no}
                              {r.company_name ? ` · ${r.company_name}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="center"><Pill v={r.worked} kind="worked" /></TableCell>
                          <TableCell align="center"><Pill v={r.paidLeave} kind="paidLeave" /></TableCell>
                          <TableCell align="center"><Pill v={r.lop} kind="lop" /></TableCell>
                          <TableCell align="center"><Pill v={r.permission} kind="permission" /></TableCell>
                          <TableCell align="center"><Pill v={r.compOff} kind="compOff" /></TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpanded(expanded === r.employee_id ? null : r.employee_id)
                              }
                            >
                              {expanded === r.employee_id ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, borderBottom: "none", background: "#fbfcfe" }}>
                            <Collapse in={expanded === r.employee_id} unmountOnExit>
                              <Box sx={{ px: 1, py: 1 }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#4a5568", mb: 1 }}>
                                  {r.employee_name} · {cycleLabel}
                                </Typography>
                                {expanded === r.employee_id && (
                                  <DayStrip empId={r.employee_id} cycleDate={anchor.format("YYYY-MM-DD")} />
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ fontSize: 11.5, color: "#8a97ad", mt: 1.5 }}>
              Showing {filtered.length} employees · pill colours match the mobile calendar · click a row's arrow for its day strip
            </Typography>
          </>
        )}
      </Grid>
    </Layout>
  );
}

export default AllEmployeesAttendance;
